'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import Portal from './Portal'
import {
  FaLock,
  FaSearch,
  FaTimes,
  FaExclamationTriangle,
} from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'next/navigation'
import { loras } from '../helpers/config'
import { poseOwnThumb, poseThumb } from '../helpers/poseThumb'
import { safeGetItem, safeSetItem } from '../helpers/safeStorage'
import { useAuth } from '../contexts/AuthContext'
import useFlag from '../hooks/useFlag'
import VoiceSettings from './VoiceSettings'
import { isV2Media } from '../lib/api/media-ids.ts'
import {
  LTX_DIALOGUE_ACCENT_DEFAULT,
  LTX_DIALOGUE_SPEAKER_DEFAULT,
  LTX_DIALOGUE_TONE_DEFAULT,
  VIDEO_V4,
  dialogueWordCount,
  isLtx,
  ltxDialogueLimits,
} from '../helpers/videoVersions'

const CURRENT_POSE_LORA = {
  title: 'Same Pose',
  src: '/assets/loras/continue.webp',
  animeSrc: '/assets/loras/continue.webp',
  plan: 'Free',
}

// "Same Pose" is the default/recommended selection — pre-selected on open and when the user
// taps the Same Pose tab, so Animate is ready without an extra tap. Shape matches selectPose.
const SAME_POSE_SELECTION = {
  title: 'Same Pose',
  mode: 'continue',
  pose: null,
  freestyle: false,
}

// Extend-only "freestyle" poses. They use a generic LoRA so the user's Motion prompt drives the
// scene (a specific pose's own LoRA otherwise dominates). Kept out of the shared `loras` list so
// they only appear in the extend picker, not normal generation. `title` must match the backend
// var/loras_data.py keys (sent as `pose`); `label` is the display-only name. TODO: swap in art.
// A single extend-only "Custom" pose: a generic LoRA so the user's Motion prompt drives the scene.
// It shows under every filter tab; the resolved backend pose is decided in handleAnimate from the
// active filter (gender) + the hardcore/softcore toggle (sex vs safe model). Kept out of the shared
// `loras` list so it only appears in the extend picker. TODO: swap in dedicated thumbnail art.
const EXTEND_FREESTYLE_LORAS = [
  {
    title: 'Custom',
    src: '/assets/loras/random.webp',
    animeSrc: '/assets/loras/random.webp',
    plan: 'Free',
    categories: ['Straight', 'Gay', 'Lesbian'],
    freestyle: true,
  },
]

// Custom selection -> backend pose key (must exist in var/loras_data.py). Gender from the
// orientation dropdown; softcore always maps to the gender-neutral safe base.
const resolveCustomPose = (gender, hardcore) => {
  if (!hardcore) return 'General SFW'
  if (gender === 'Gay') return 'General Gay'
  if (gender === 'Lesbian') return 'General Lesbian'
  return 'General NSFW' // Straight
}

const ExtendModal = ({
  isOpen,
  closeModal,
  subscription,
  model,
  setIsLimitModalOpen,
  setErrorText,
  user = false,
  handleSubmitVideoV3,
  extendId,
  // The SOURCE video's engine. v4's extend pipeline has no dialogue, so the spoken line is
  // offered only when the source is v5.
  sourceVersion = VIDEO_V4,
  // Set when this opening is a Redo: the choices the redone extension was made with (the
  // `selection` handleAnimate sends), restored so retrying is one tap. Null for a fresh extend.
  initial = null,
}) => {
  const { t } = useTranslation()
  const router = useRouter()
  const { isPlusOrHigher, isPremiumOrHigher, isStudioOrHigher } = useAuth()

  const [shouldRender, setShouldRender] = useState(isOpen)
  const [filter, setFilter] = useState(
    () => safeGetItem('filter') || 'Straight',
  )
  const [filteredLoras, setFilteredLoras] = useState([])
  const [searchedLoras, setSearchedLoras] = useState([])
  const [searchInput, setSearchInput] = useState('')
  const [changePoseOpen, setChangePoseOpen] = useState(false)
  const [selectedPose, setSelectedPose] = useState(null) // { title, mode, pose }
  const [motionPrompt, setMotionPrompt] = useState('')
  const [dialogue, setDialogue] = useState('')
  const [dialogueSpeaker, setDialogueSpeaker] = useState(
    LTX_DIALOGUE_SPEAKER_DEFAULT,
  )
  const [dialogueTone, setDialogueTone] = useState(LTX_DIALOGUE_TONE_DEFAULT)
  const [dialogueAccent, setDialogueAccent] = useState(
    LTX_DIALOGUE_ACCENT_DEFAULT,
  )
  const [hardcore, setHardcore] = useState(true) // Custom: hardcore=sex model, off=safe model
  const [customGender, setCustomGender] = useState('Straight') // Custom orientation dropdown
  // Whether this opening is a Redo. Latched at open (not read off `initial`, which the parent
  // clears on close) so the header doesn't flip to "Extend" during the close fade.
  const [isRedo, setIsRedo] = useState(false)
  // The line the user had typed before a Redo restored the redone one, given back on the next
  // plain open — a Redo must not leave its line behind in an unrelated extend.
  const preRedoLineRef = useRef(null)

  // An extension is ALWAYS its own ~5.83s window regardless of how long the source clip was, so
  // the word budget is the 6s one — it must not inherit a 10s source's larger allowance.
  const canSpeak = isLtx(sourceVersion)
  // In the flag an extension is delivered as its own clip (promptchan-backend myapp/extend_clips.py); asked only
  // while the modal is open, so the Create page asks nothing of the backend for it on a visit that never extends.
  const asClip = useFlag('extend_clips', isOpen)
  const dialogueLimits = ltxDialogueLimits(6)
  const dialogueWords = dialogueWordCount(dialogue)
  const dialogueOverLimit = dialogueWords > dialogueLimits.words

  useEffect(() => {
    safeSetItem('filter', filter)
  }, [filter])

  // NEW: make a UI list that prepends "Same Pose" + the extend-only freestyle poses, without
  // mutating the shared `loras` list.
  const uiLoras = useMemo(() => {
    return [CURRENT_POSE_LORA, ...EXTEND_FREESTYLE_LORAS, ...loras]
  }, [])

  useEffect(() => {
    if (!filter || filter === 'All') {
      setFilteredLoras(uiLoras)
    } else {
      const filtered = uiLoras.filter(
        (l) => l?.title === 'Same Pose' || l?.categories?.includes(filter),
      )
      setFilteredLoras(filtered)
    }
  }, [filter, uiLoras])

  useEffect(() => {
    function filterImagesBySearchTerm(images, searchTerm) {
      return images.filter((image) =>
        image?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()),
      )
    }

    if (searchInput && searchInput.length > 0) {
      setSearchedLoras(filterImagesBySearchTerm(filteredLoras, searchInput))
    } else {
      setSearchedLoras(filteredLoras)
    }
  }, [filteredLoras, searchInput])

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true)
      const pose = initial?.selectedPose || SAME_POSE_SELECTION
      setChangePoseOpen(pose.mode === 'change')
      setSelectedPose(pose)
      setMotionPrompt(initial?.motionPrompt || '')
      setHardcore(initial?.hardcore ?? true)
      setCustomGender(initial?.customGender || 'Straight')
      setIsRedo(!!initial)
      // A fresh extend keeps whatever line was last typed; a Redo of a voice clip restores the
      // redone one (a v4 clip has no line, so its Redo leaves the typed one alone).
      if (initial && canSpeak) {
        if (!preRedoLineRef.current) {
          preRedoLineRef.current = {
            dialogue,
            dialogueSpeaker,
            dialogueTone,
            dialogueAccent,
          }
        }
        setDialogue(initial.dialogue || '')
        setDialogueSpeaker(
          initial.dialogueSpeaker || LTX_DIALOGUE_SPEAKER_DEFAULT,
        )
        setDialogueTone(initial.dialogueTone || LTX_DIALOGUE_TONE_DEFAULT)
        setDialogueAccent(initial.dialogueAccent || LTX_DIALOGUE_ACCENT_DEFAULT)
      } else if (!initial && preRedoLineRef.current) {
        const line = preRedoLineRef.current
        preRedoLineRef.current = null
        setDialogue(line.dialogue)
        setDialogueSpeaker(line.dialogueSpeaker)
        setDialogueTone(line.dialogueTone)
        setDialogueAccent(line.dialogueAccent)
      }
    }
  }, [isOpen, initial])

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'unset'
  }, [isOpen])

  const getColorForPlan = (plan = 'Free') => {
    switch (plan) {
      case 'Pro':
      case 'Studio':
        return '#FFB419'
      case 'Premium':
        return '#7ED6FF'
      case 'Plus':
        return '#E54B00'
      default:
        return '#FFFFFF'
    }
  }

  const canSelectImage = (imagePlan) => {
    switch (imagePlan) {
      case 'Free':
        return true
      case 'Plus':
        return isPlusOrHigher
      case 'Premium':
        return isPremiumOrHigher
      case 'Pro':
      case 'Studio':
        return isStudioOrHigher
      default:
        return false
    }
  }

  const selectPose = (e, image) => {
    e.preventDefault()

    if (!canSelectImage(image.plan)) {
      router.push('/upgrade')
      return
    }

    // "Same Pose" continues the source video's pose; any other pose is a change. Selection only —
    // generation waits for the explicit Animate button so the motion prompt can be set first.
    const extendMode = image.title === 'Same Pose' ? 'continue' : 'change'
    const pose = extendMode === 'change' ? image.title : null
    setSelectedPose({
      title: image.title,
      label: image.label, // display-only name (backend still gets `title`)
      mode: extendMode,
      pose,
      // Motion prompt + orientation/hardcore controls only apply to the generic "Custom" pose.
      freestyle: !!image.freestyle,
    })
  }

  const handleAnimate = () => {
    if (!selectedPose) return
    // Custom resolves to a concrete backend pose from the orientation dropdown + hardcore toggle.
    const pose = selectedPose.freestyle
      ? resolveCustomPose(customGender, hardcore)
      : selectedPose.pose
    handleSubmitVideoV3({
      extend: true,
      extendMode: selectedPose.mode,
      pose,
      extendVideoId: extendId,
      // Only send the motion prompt for the Custom pose (it's hidden / inert otherwise).
      motionPrompt: selectedPose.freestyle ? motionPrompt.trim() || null : null,
      // v5 only — the field isn't rendered otherwise, and the backend ignores it on a v4 source.
      dialogue: canSpeak ? dialogue.trim() || null : null,
      dialogueSpeaker,
      dialogueTone,
      dialogueAccent,
      // Not sent to the backend: kept on the result card so its Redo can restore these.
      asClip,
      selection: {
        selectedPose,
        motionPrompt,
        hardcore,
        customGender,
        dialogue: canSpeak ? dialogue : '',
        dialogueSpeaker,
        dialogueTone,
        dialogueAccent,
      },
    })
    closeModal()
  }

  // Tab handlers. Same Pose is auto-selected (it's Free/always allowed). Switching to Change
  // Pose clears a Same Pose selection so the user must pick from the grid, but keeps any grid
  // pose they'd already chosen.
  const showSamePose = () => {
    setChangePoseOpen(false)
    setSelectedPose(SAME_POSE_SELECTION)
  }

  const showChangePose = () => {
    setChangePoseOpen(true)
    setSelectedPose((prev) =>
      prev && prev.title !== 'Same Pose' ? prev : null,
    )
  }

  // Poses available when changing pose (everything except the "Same Pose" entry).
  const changePoses = searchedLoras
    .filter((image) => {
      if (image.title === 'Same Pose') return false
      if (image.title === 'Default') return false
      if (image.hideToFree && (!user || !isPlusOrHigher)) return false
      if (image.hideToReviewer && subscription?.source === 'Reviewer')
        return false
      if (image.enabled && !image.enabled.includes(model)) return false
      return true
    })
    // Pin the freestyle poses to the top so they're visible immediately, then alphabetical.
    .sort((a, b) => {
      if (!!a.freestyle !== !!b.freestyle) return a.freestyle ? -1 : 1
      return a.title.localeCompare(b.title)
    })

  if (!shouldRender) {
    return null
  }

  return (
    <Portal>
      <div
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            closeModal()
          }
        }}
        className={`fixed z-[110] top-0 left-0 w-full h-full bg-black/70 p-2 sm:p-3 flex justify-center items-center ${
          isOpen ? 'opacity-100' : 'opacity-0'
        } transition-opacity duration-200 ${
          isOpen ? 'pointer-events-auto' : 'pointer-events-none'
        }`}
      >
        <div
          className={`bg-[var(--middle-bg-color)] ${
            changePoseOpen
              ? 'h-[92vh] sm:h-[90vh]'
              : 'max-h-[92vh] sm:max-h-[85vh]'
          } w-full max-w-lg md:w-2/3 md:max-w-none flex flex-col overflow-hidden min-h-0 rounded-2xl border border-white/10 shadow-2xl p-4 sm:p-5 text-gray-100 space-y-3 relative transform transition-all duration-200 ${
            isOpen ? 'scale-100 opacity-100' : 'scale-70 opacity-0'
          }`}
        >
          <header className="pr-9">
            <h2 className="flex items-center gap-2 text-xl font-extrabold text-shadow sm:text-2xl">
              {isRedo ? 'Try again' : 'Extend'}
            </h2>
            {/* A Redo keeps its explanation on both tabs: it says the result replaces the card. */}
            {(isRedo || !changePoseOpen) && (
              <h3 className="mt-1 text-xs font-medium leading-snug text-gray-400 sm:text-sm">
                {isRedo
                  ? 'Tries again from the clip before, with your last choices. The new result replaces this one in your feed.'
                  : 'Continue the video using the Same Pose or choose a new one.'}
                {!changePoseOpen &&
                  ' Quality is inherited from the original video — 15 gems for Normal, 25 for High Quality.'}
              </h3>
            )}

            <button
              onClick={closeModal}
              className="absolute right-2 top-2 text-white/60 transition-colors hover:text-white sm:right-3 sm:top-3"
              style={{ fontSize: '1.4rem' }}
            >
              <FaTimes />
            </button>
          </header>
          <hr className="border-gray-800" />

          {/* ===== Same Pose / Change Pose — segmented tabs ===== */}
          <div className="grid grid-cols-2 gap-1 rounded-xl border border-white/10 bg-[var(--foreground-bg-color)] bg-opacity-40 p-1">
            <button
              type="button"
              onClick={showSamePose}
              className={`rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors ${
                !changePoseOpen
                  ? 'bg-[var(--main-bg-color)] text-white shadow'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              Same Pose
            </button>
            <button
              type="button"
              onClick={showChangePose}
              className={`rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors ${
                changePoseOpen
                  ? 'bg-[var(--main-bg-color)] text-white shadow'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              Change Pose
            </button>
          </div>

          {/* ===== Same Pose tab ===== */}
          {!changePoseOpen && (
            <div className="flex items-center gap-3 rounded-xl bg-[var(--foreground-bg-color)] bg-opacity-30 p-3">
              <img
                src={CURRENT_POSE_LORA.src}
                alt="Same Pose"
                className="h-12 w-12 shrink-0 rounded-lg object-cover sm:h-14 sm:w-14"
              />
              <p className="text-sm text-gray-300">
                Continues the exact pose from your video for the smoothest, most
                consistent result.
              </p>
            </div>
          )}

          {/* ===== Change Pose tab ===== */}
          {changePoseOpen && (
            <div className="flex min-h-0 flex-grow flex-col space-y-3">
              {/* Disclaimer — light inline note, not a heavy boxed banner */}
              <p className="flex items-start gap-2 text-xs leading-snug text-amber-200/80">
                <FaExclamationTriangle className="mt-0.5 shrink-0 text-amber-400" />
                <span>
                  Pick a pose close to the one in your video — big changes can
                  cause artifacts.
                </span>
              </p>

              {/* Search + filter */}
              <div className="flex items-center gap-2">
                <div className="relative flex-1 md:w-1/3 md:flex-none">
                  <input
                    type="search"
                    placeholder="Search"
                    className="w-full rounded-full border border-gray-500 bg-[var(--middle-bg-color)] py-2 pl-10 pr-3 text-white focus:border-white focus:outline-none"
                    onChange={(e) => setSearchInput(e.target.value)}
                    value={searchInput}
                  />
                  {searchInput && (
                    <span className="absolute right-3.5 top-3.5 ml-3.5 block text-gray-400 md:hidden">
                      <FaTimes onClick={() => setSearchInput('')} />
                    </span>
                  )}
                  <span className="absolute left-0 top-3 ml-3.5 text-gray-400">
                    <FaSearch />
                  </span>
                </div>

                {/* Filter dropdown */}
                <div className="relative w-32 shrink-0 md:w-1/5">
                  <select
                    id="filter"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="w-full appearance-none rounded-md border border-gray-500 bg-[#0F0D14] px-3 py-2 text-white focus:border-white focus:outline-none"
                  >
                    <option value="Straight"> {t('Straight')}</option>
                    <option value="Lesbian"> {t('Lesbian')}</option>
                    <option value="Gay"> {t('Gay')}</option>
                    <option value="All"> {t('All')}</option>
                  </select>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex -translate-y-1 items-center pr-5 text-lg text-gray-400">
                    ⌄
                  </span>
                </div>
              </div>

              {/* Pose grid */}
              <main className="grid min-h-0 flex-grow grid-cols-2 gap-1 overflow-y-auto overflow-x-hidden p-1 sm:grid-cols-3 lg:grid-cols-5">
                {changePoses.length === 0 && (
                  <div className="col-span-full my-auto p-8 text-center text-gray-300">
                    No Poses found. Try reducing your search terms.
                  </div>
                )}

                {changePoses.map((image) => {
                  return (
                    <div
                      key={image.title}
                      className={`m-1 border-2 relative rounded-xl transition-all duration-200 ease-in-out hover:scale-[1.03] cursor-pointer
        ${
          selectedPose?.title === image.title
            ? 'border-[var(--main-bg-color)] shadow-lg'
            : canSelectImage(image.plan)
              ? 'border-transparent hover:shadow-lg hover:border-gray-800 '
              : 'border-transparent'
        }`}
                      onClick={(e) => selectPose(e, image)}
                    >
                      {image.videoSrc ? (
                        <video
                          src={image.videoSrc}
                          className={`w-full h-full object-cover rounded-xl ${
                            canSelectImage(image.plan) ? '' : 'opacity-80'
                          }`}
                          loop
                          autoPlay
                          muted
                          playsInline
                          webkit-playsinline="true"
                          x5-playsinline="true"
                          preload="auto"
                        />
                      ) : (
                        <img
                          src={poseThumb(image, model)}
                          alt={image.title}
                          className={`w-full h-full object-cover rounded-xl ${
                            canSelectImage(image.plan) ? '' : 'opacity-80'
                          }`}
                          onError={(e) => {
                            // Same model-aware fallback as LoraModal: an unconditional
                            // `image.src` here served the legacy photoreal thumbnail to anime
                            // models whenever the ponyAnime* file was missing.
                            e.target.src = poseOwnThumb(image, model)
                          }}
                        />
                      )}

                      {image.freestyle && (
                        <span className="absolute left-1 top-1 z-10 whitespace-nowrap rounded bg-[var(--main-bg-color)] px-1.5 py-0.5 text-[9px] font-bold text-white shadow">
                          Motion Prompt
                        </span>
                      )}

                      {!canSelectImage(image.plan) ? (
                        <div
                          className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 rounded-xl border-2"
                          style={{ borderColor: getColorForPlan(image.plan) }}
                        >
                          <span
                            className="flex items-center gap-1 rounded-full border bg-black/60 px-2.5 py-1 text-xs font-bold"
                            style={{
                              color: getColorForPlan(image.plan),
                              borderColor: getColorForPlan(image.plan),
                            }}
                          >
                            <FaLock /> {image.plan}
                          </span>

                          <p className="font-semibold absolute bottom-0 mb-2 ml-2 text-gray-300 text-sm bg-black bg-opacity-50 rounded px-1">
                            {image.label || t(image.title)}
                          </p>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center">
                          <p className="font-semibold absolute bottom-0 mb-2 ml-2 text-white text-sm bg-black bg-opacity-50 rounded px-1">
                            {image.label || t(image.title)}
                          </p>
                        </div>
                      )}
                    </div>
                  )
                })}
              </main>
            </div>
          )}

          {/* ===== Motion prompt + Animate (explicit submit) ===== */}
          <div className="shrink-0 space-y-2 border-t border-gray-800 pt-3">
            {/* Custom pose only: hardcore/softcore toggle + a motion prompt that drives the scene.
              Hidden for preset / Same Pose selections where the motion prompt has little effect. */}
            {selectedPose?.freestyle && (
              <>
                {/* Custom controls: hardcore/softcore switch on the left, orientation dropdown
                  (hardcore only) on the right. Backend pose computed from these in handleAnimate. */}
                <div className="flex flex-wrap items-center justify-between gap-x-5 gap-y-2">
                  {/* switch borrowed from Inputs.jsx, kept next to its label so they read together */}
                  <div className="flex items-center gap-2">
                    <div
                      className={`relative inline-block w-12 h-6 rounded-full transition-colors duration-200 ease-out cursor-pointer shrink-0 ${
                        hardcore
                          ? 'bg-[var(--main-bg-color)] ring-1 ring-inset ring-white/15'
                          : 'bg-[var(--foreground-bg-color)]'
                      }`}
                      onClick={() => setHardcore((prev) => !prev)}
                      role="switch"
                      aria-checked={hardcore}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 inline-block w-5 h-5 rounded-full shadow transform transition-transform duration-200 ease-out ${
                          hardcore
                            ? 'translate-x-6 bg-white'
                            : 'translate-x-0 bg-gray-500'
                        }`}
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-300">
                      {hardcore ? 'Hardcore' : 'Softcore'}
                    </span>
                    <span className="hidden text-xs text-gray-500 sm:inline">
                      {hardcore ? 'fully explicit' : 'softer, suggestive'}
                    </span>
                  </div>
                  {hardcore && (
                    <label className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-300">
                        Orientation
                      </span>
                      <select
                        value={customGender}
                        onChange={(e) => setCustomGender(e.target.value)}
                        className="rounded-md border border-gray-600 bg-[var(--middle-bg-color)] px-2 py-1.5 text-sm text-white focus:border-white focus:outline-none"
                      >
                        <option value="Straight">Straight</option>
                        <option value="Gay">Gay</option>
                        <option value="Lesbian">Lesbian</option>
                      </select>
                    </label>
                  )}
                </div>
                <label
                  htmlFor="motionPrompt"
                  className="block text-sm font-medium text-gray-300"
                >
                  Motion prompt{' '}
                  <span className="text-gray-500">(optional)</span>
                </label>
                <textarea
                  id="motionPrompt"
                  rows={2}
                  value={motionPrompt}
                  maxLength={500}
                  onChange={(e) => setMotionPrompt(e.target.value)}
                  placeholder="Describe the motion for the new part…"
                  className="w-full resize-none rounded-lg border border-gray-600 bg-[var(--middle-bg-color)] p-2.5 text-sm text-white focus:border-white focus:outline-none"
                />
              </>
            )}
            {canSpeak && (
              <div className="mt-1">
                <label
                  htmlFor="extendDialogue"
                  className="block text-sm font-medium text-gray-300"
                >
                  {t('dialogue')}{' '}
                  <span className="text-gray-500">({t('optional')})</span>
                </label>
                <textarea
                  id="extendDialogue"
                  rows={2}
                  value={dialogue}
                  maxLength={dialogueLimits.chars}
                  onChange={(e) => setDialogue(e.target.value)}
                  placeholder={t('extendDialoguePlaceholder')}
                  className="mt-1 w-full resize-none rounded-lg border border-gray-600 bg-[var(--middle-bg-color)] p-2.5 text-sm text-white focus:border-white focus:outline-none"
                />
                <div className="mt-2 flex items-center justify-between gap-2">
                  <VoiceSettings
                    className="min-w-0"
                    dialogueSpeaker={dialogueSpeaker}
                    setDialogueSpeaker={setDialogueSpeaker}
                    dialogueTone={dialogueTone}
                    setDialogueTone={setDialogueTone}
                    dialogueAccent={dialogueAccent}
                    setDialogueAccent={setDialogueAccent}
                  />
                  <span
                    className={`shrink-0 text-xs tabular-nums ${
                      dialogueOverLimit ? 'text-amber-400' : 'text-gray-400'
                    }`}
                  >
                    {dialogueWords}/{dialogueLimits.words}
                  </span>
                </div>
                <p className="mt-1.5 text-xs leading-relaxed text-gray-400">
                  {/* A line on one of v2's clips: v2 doesn't take one yet, and v1 can't find v2's clips, so the
                      submit is refused (src/lib/api/video-v2.ts, notYetOnV2). Said here first. */}
                  {isV2Media(extendId) && dialogue.trim() ? (
                    <span role="alert" className="text-amber-200">
                      {t('videoLineNotYetClip')}
                    </span>
                  ) : (
                    t('extendDialogueHint')
                  )}
                </p>
              </div>
            )}
            <button
              onClick={handleAnimate}
              // No source, no extend: never send one the modal wasn't given (12 October 2026)
              disabled={!selectedPose || !extendId}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[var(--main-bg-color)] py-3 text-base font-bold text-white shadow-md transition-transform duration-200 enabled:hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {selectedPose
                ? `Animate — ${selectedPose.label || selectedPose.title}`
                : 'Select a pose to continue'}
            </button>
          </div>
        </div>
      </div>
    </Portal>
  )
}

export default ExtendModal
